/**
 * Client and API logging → data delivery's addin-logs API.
 *
 * WHY THIS EXISTS SEPARATELY FROM logger.ts
 * -----------------------------------------
 * nginx already records every API call server-side (the $insync_activity map in
 * docker/nginx/default.conf), so API-backed work is visible without any client
 * help. What is NOT visible is everything that never reaches the server:
 *
 *   • Broker Revisions on a cache hit — formulas resolve from the runtime cache,
 *     no HTTP at all
 *   • Unlink / Relink — pure in-sheet manipulation
 *   • Refresh when every formula is cached
 *   • ISTARI Web / InSync MCP — window.open, never touches our origin
 *   • WHO logged in: nginx logs POST /addin/login with user_email EMPTY by
 *     design, because credentials live in the unlogged POST body
 *
 * For those, a client-side event is the only record that will ever exist.
 * API request/response/error records also use this delivery path, but retain
 * their separate `request` stream.
 *
 * SHAPE
 * -----
 * The endpoint takes a FLAT JSON ARRAY (not the {type,stream,records} envelope
 * the design doc describes), with `type` and `stream` as per-record fields:
 *
 *   [ { event_id, ts, type:"prod", stream:"client request", level, context,
 *       message, data, error, session_id, request_id, app_version, platform,
 *       user_email }, ... ]
 *
 * `stream` is "client request" WITH A SPACE. The column is
 * LowCardinality(String) so "client_request" would also insert silently and
 * then never match a dashboard filter — do not "correct" the spelling.
 *
 * `clock_skew_s` is MEASURED, not computed from received_at: a producer cannot
 * know received_at, so the value comes from comparing the local clock against
 * the Date header on our own POST responses. The design doc has this column as
 * MATERIALIZED, which is better - if data delivery makes it so, ClickHouse will
 * reject an INSERT naming it and this field must be dropped from the record.
 *
 * Records are batched by COUNT (see FLUSH_BATCH_SIZE) rather than on a short
 * interval, so a busy session produces one POST per hundred actions instead of
 * one every ten seconds. The age cap is only a tail-guard for sessions that
 * never reach the count.
 *
 * Nothing in this file may throw. Logging must never break the add-in.
 */

import {
  API_LOGGING_ENABLED,
  API_LOGGING_ENDPOINT,
  APP_VERSION,
  IS_PROD,
  LOG_SAVE_AUTH,
  TEMP_TOKEN,
} from './config';
import { isIdleExpired } from '../core/auth/idleSession';
import { isWafBlocked, WAF_BACKOFF_MS } from './waf';
import { isSessionEnded } from './sessionEnded';
import type { LogEntry, LogLevel } from './logger';

/**
 * Credential VALUES that must never reach the log store.
 *
 * This is deliberately value-based rather than field-name-based. The existing
 * redaction in logger.ts catches `username_filter=…` because of its NAME, which
 * only works where we anticipated the field. Matching on the value catches the
 * same secret wherever it turns up — inside a URL, an error string a backend
 * echoed back, a stringified request body, a param nobody thought about.
 *
 * TEMP_TOKEN is the only credential still compiled into the bundle: it is the
 * fallback username_filter, so it travels as an ordinary-looking query value on
 * many requests. The backend Basic credentials (DD_BASIC_AUTH /
 * CONTACT_BASIC_AUTH) live only in nginx since VAPT V-001 and never reach the
 * browser, so there is nothing of theirs to scrub here. The per-user edge token
 * is scrubbed too, but read at redaction time (see redactSecrets) because it
 * changes on every login.
 *
 * Short values are skipped. A 1- or 2-character secret would match constantly
 * and shred unrelated text; anything that short is not a credential worth
 * protecting anyway.
 */
const SECRET_VALUES: string[] = [
  TEMP_TOKEN,
]
  .filter((v): v is string => typeof v === 'string' && v.length >= 8)
  // Longest first, so a secret that contains another is scrubbed whole rather
  // than leaving a recognisable tail behind.
  .sort((a, b) => b.length - a.length);

/** Same key tokenStorage mirrors the edge token under (STORAGE_KEYS.EDGE_TOKEN). */
const EDGE_TOKEN_KEY = 'iad_edge_token';

/**
 * The edge session token (VAPT V-001), read synchronously - the shipper never
 * awaits. tokenStorage is required lazily, as in hydrateUser, because importing
 * it (or httpClient) at the top would close the logger -> actionLog cycle. Falls
 * back to the localStorage mirror so the custom-functions runtime, which has its
 * own tokenStorage instance with a cold cache, can still send.
 */
function edgeToken(): string | null {
  try {
    // An idle-expired OTP session is logged out for data calls; logs included.
    if (isIdleExpired()) return null;
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const ts = require('../core/auth/tokenStorage')?.tokenStorage;
    let token: string | null = ts?.getCachedEdgeToken?.() ?? null;
    if (!token && typeof localStorage !== 'undefined') token = localStorage.getItem(EDGE_TOKEN_KEY);
    return token ? token.replace(/[\r\n]/g, '') : null;
  } catch {
    return null;
  }
}

/**
 * Request headers for a log POST, or null when it cannot succeed yet.
 *
 * /consensus_v2/* sits behind nginx's auth_request gate (VAPT V-001): nginx
 * checks X-Insync-Token, then injects the data-delivery Basic credential chosen
 * by the non-secret X-Insync-Auth marker - the same contract httpClient's
 * credMarker: 'dd' clients use. No credential is sent from the browser.
 *
 * The API also REQUIRES ?username_filter= (422 "Field required" without it), and
 * a rejected batch is gone for good. So no filter means hold, exactly like no
 * edge token: the records wait for the first flush that can actually land.
 */
function logPostHeaders(): Record<string, string> | null {
  const token = edgeToken();
  if (!token || !usernameFilter()) return null;
  return {
    'Content-Type': 'application/json',
    'X-Insync-Auth': 'dd',
    'X-Insync-Token': token,
  };
}

/**
 * Replace every occurrence of a known credential with a marker.
 *
 * Disabled by LOG_SAVE_AUTH=1, which exists only for local debugging of an auth
 * problem. Never throws: a logging helper that can fail takes the add-in with it.
 */
function redactSecrets(value: string): string {
  if (LOG_SAVE_AUTH === 1 || !value) return value;
  try {
    const edge = edgeToken();
    const secrets = edge && edge.length >= 8 ? [edge, ...SECRET_VALUES] : SECRET_VALUES;
    if (secrets.length === 0) return value;
    let out = value;
    for (const secret of secrets) {
      if (out.includes(secret)) out = out.split(secret).join('***');
      // URLs carry them percent-encoded, which would otherwise slip straight past.
      const enc = encodeURIComponent(secret);
      if (enc !== secret && out.includes(enc)) out = out.split(enc).join('***');
    }
    return out;
  } catch {
    // Fail closed: if scrubbing breaks, drop the text rather than ship a secret.
    return '[redaction-failed]';
  }
}

/**
 * How far this machine's clock is from the server's, in seconds. Positive means
 * the client is AHEAD.
 *
 * Measured from the Date header on our own log POSTs rather than guessed: a
 * producer cannot compute received_at - ts, because only the API sets
 * received_at. Sending a made-up difference would be worse than sending nothing,
 * since it would look authoritative in a dashboard.
 *
 * Stays 0 until the first response comes back, which is honest — before that the
 * skew genuinely is unknown. Once measured it applies to every later record.
 */
let clockSkewSeconds = 0;
let clockSkewMeasured = false;

/**
 * Derive the offset from a response's Date header.
 *
 * Date has one-second resolution and is stamped when the response is generated,
 * so round-trip time shows up as error. That is irrelevant here: this field
 * exists to catch machines that are minutes or years out, not to measure
 * sub-second drift.
 */
function captureClockSkew(res: { headers?: { get?: (n: string) => string | null } }): void {
  try {
    const dateHeader = res?.headers?.get?.('date');
    if (!dateHeader) return;
    const serverMs = Date.parse(dateHeader);
    if (!Number.isFinite(serverMs)) return;
    clockSkewSeconds = Math.round((Date.now() - serverMs) / 1000);
    clockSkewMeasured = true;
  } catch {
    /* never throw from logging */
  }
}

/** Exposed for the dev console hook, so a wrong clock is visible while testing. */
export function getClockSkewSeconds(): number {
  return clockSkewSeconds;
}

/** prod|dev — selects the target database. Derived from IS_PROD. */
const LOG_TYPE = IS_PROD === 1 ? 'prod' : 'dev';

/** "client request" — with a space. See the header note. */
const CLIENT_STREAM = 'client request';

const SHIP_LEVELS: ReadonlyArray<LogLevel> = ['info', 'warn', 'error'];

/**
 * BATCHING POLICY
 * ---------------
 *  batching at 100 collapses that into roughly one POST per hundred
 * actions, which is the difference between a chatty endpoint and a quiet one.
 *
 */
const FLUSH_BATCH_SIZE = 100;
const FLUSH_MAX_AGE_MS = 600000;

const BUFFER_KEY = 'insync_log_buffer_v1';
const BUFFER_PERSIST_CAP = 300;

/**
 * Pane switches typically accumulate only a handful of records, so they stay
 * silent and the batching still holds. A user who has done real work and is
 * quitting does not have their session stranded in localStorage waiting for a
 * return visit that may never happen - or that happens on another machine,
 * where this storage does not exist.
 */
const TEARDOWN_SEND_FLOOR = 25;

/**
 * Body budget for a teardown send. keepalive requests are capped at 64KB by the
 * browser and REJECTED outright above it - so a full 100-record batch could
 * silently fail exactly when there is no chance to retry. Anything over this
 * stays parked for the next session instead.
 */
const KEEPALIVE_MAX_BYTES = 60000;

/**
 * How long restored records wait before going out. Short, because they are
 * already old - but not zero, so a pane switch still merges its records into one
 * batch rather than firing a POST the instant the new pane loads.
 */
const RESTORED_FLUSH_DELAY_MS = 15000;
const ACTIVITY_BY_CONTEXT: Record<string, string> = {
  // Matches the COM add-in's success record, so one filter finds both.
  'Auth.LoggedIn': 'LoginSucceeded',
  'Auth.LoggedOut': 'Logout',
};

const ACTIVITY_BY_MODULE: Record<string, string> = {
  // ---- matches an nginx label exactly; do not re-spell ----
  IAD: 'Data / Formula',        // /consensus_v2/get_add_in_data
  IBD: 'Data / Formula',
  IGD: 'Data / Formula',
  Models: 'Models',             // (/v2)?/addin-model-download, /cloud_download (model files)
  DynamicApi: 'Dynamic API',    // /cloud_download/.../Dynamic_API_Sheet/...
  IstariWeb: 'ISTARI Web',      // ribbon button, opens the browser (no nginx route)
  Contact: 'Contact Support',   // /micro
  StockPrices: 'Stock Prices',  // /legacy-api
  // ---- client-only: no server route, so no nginx label to match ----
  Ribbon: 'Ribbon',
  Comps: 'Comps',                          // also an nginx label (/cloud_download .../istari/comps)
  SourceDocuments: 'Source Documents',     // also an nginx label (/cloud_download .../filings/source_documents)
  BrokerRevisions: 'Broker Revisions',
  InsertData: 'Insert Data',
  InsertFormula: 'Insert Formula',
  Refresh: 'Refresh',
  Unlink: 'Unlink',
  Relink: 'Relink',
  Audit: 'Audit',
  Profile: 'Profile',
  InSyncMcp: 'InSync MCP',
};

let currentActivity = '';

/**
 * Dimensions merged into EVERY event's `data`, so any log row can be sliced by
 * product, pane or ticker without the analyst having to know which call site
 * happened to include them. Explicit data at the call site always wins over the
 * ambient value.
 *
 * Kept small on purpose: `data` is capped at 4000 chars and these are paid for
 * on every single row.
 */
const AMBIENT_BASE: Record<string, unknown> = {
  add_in: 'Insync Consensus',
};

let ambient: Record<string, unknown> = { ...AMBIENT_BASE };

/**
 * Merge dimensions into every subsequent event — e.g. the active pane, or the
 * ticker the user has selected. Pass null/undefined for a key to drop it, so a
 * stale ticker does not follow the user into an unrelated pane.
 */
export function setAmbientContext(patch: Record<string, unknown>): void {
  try {
    const next = { ...ambient };
    for (const [k, v] of Object.entries(patch ?? {})) {
      if (v === null || v === undefined || v === '') delete next[k];
      else next[k] = v;
    }
    ambient = next;
  } catch {
    /* never throw from logging */
  }
}

/** Reset to just the constants — used when a session ends. */
export function resetAmbientContext(): void {
  ambient = { ...AMBIENT_BASE };
}

export function getAmbientContext(): Record<string, unknown> {
  return { ...ambient };
}

/** Called by the taskpane router whenever the active pane changes. */
export function setCurrentActivity(activity: string): void {
  currentActivity = activity || '';
}

export function getCurrentActivity(): string {
  return currentActivity;
}

/**
 * Pane name (the ?pane= value) -> activity label. Pane names are the module
 * names with a lowercase first letter, so capitalising and reusing the one map
 * keeps a single spelling rather than inventing a parallel vocabulary.
 */
export function paneActivity(pane: string): string {
  if (!pane) return '';
  if (pane === 'login') return 'Login';
  if (pane === 'logout') return 'Logout';
  const key = pane.charAt(0).toUpperCase() + pane.slice(1);
  return ACTIVITY_BY_MODULE[key] ?? '';
}

function activityFor(context: string): string {
  const exact = ACTIVITY_BY_CONTEXT[context];
  if (exact) return exact;
  // Own mapping first, then the flow the user is in. Never guess beyond that:
  // an unmapped context outside any flow is better left blank than mislabelled.
  return ACTIVITY_BY_MODULE[context.split('.')[0]] ?? currentActivity ?? '';
}


// ── session identity ────────────────────────────────────────────────────────
// Regenerated on every add-in load, which is exactly the grouping we want: one
// session_id spans everything the user did in one Excel session. Pseudonymous —
// it identifies a session, not a person.
function makeId(): string {
  try {
    const c = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) {
      const b = c.getRandomValues(new Uint8Array(16));
      return Array.from(b, (x: number) => x.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

const SESSION_ID = `s_${makeId()}`;

/**
 * When this page load started. Every ribbon button reloads taskpane.html, so
 * this measures how long ONE pane was on screen - not how long Excel has been
 * open, which no single page load can know.
 */
const PAGE_LOADED_AT = Date.now();

/**
 * Office.context.platform: PC | OfficeOnline | Mac | iOS | Android. Resolved
 * lazily because Office may not be ready when this module is first imported,
 * and cached once found — a large share of add-in bugs are platform-specific
 * and this is the field that makes them separable.
 */
let cachedPlatform = '';
function platform(): string {
  if (cachedPlatform) return cachedPlatform;
  try {
    const p = (globalThis as any).Office?.context?.platform;
    if (p) cachedPlatform = String(p);
  } catch {
    /* not in an Office host, or Office not ready yet */
  }
  return cachedPlatform;
}

/**
 * The logged-in user. Read through a late `require` rather than a top-level
 * import: logger.ts is imported by tokenStorage's dependents, and importing
 * tokenStorage here would create a cycle. Sync cache only — never await on the
 * logging path.
 */
function usernameFilter(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('../core/auth/tokenStorage');
    return mod?.tokenStorage?.getUsernameFilterCached?.() ?? '';
  } catch {
    return '';
  }
}

/**
 * The endpoint with ?username_filter=… appended. Built per flush rather than
 * once at construction: the token is empty until login hydrates it, and a URL
 * captured at module load would keep sending the stale empty value forever.
 */
function endpointWithFilter(): string {
  const f = usernameFilter();
  if (!f) return API_LOGGING_ENDPOINT;
  const sep = API_LOGGING_ENDPOINT.includes('?') ? '&' : '?';
  return `${API_LOGGING_ENDPOINT}${sep}username_filter=${encodeURIComponent(f)}`;
}

function userEmail(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('../core/auth/tokenStorage');
    return mod?.tokenStorage?.getCachedUsername?.() ?? '';
  } catch {
    return '';
  }
}

// ── payload ─────────────────────────────────────────────────────────────────
export interface ActionOptions {
  level?: LogLevel;
  /** Joins this event to its server-side row. HttpClient reads X-Request-ID. */
  requestId?: string;
  error?: Error | string;
}

/**
 * `data` is capped at 4000 chars. The 1000 this used to be was inherited from
 * com-addin-logging, not from the API: addin-logs accepts a 4000-char `data`
 * (log-api-test P7.OversizeData -> 200). Room is needed for the broker list,
 * which has to sit on the same row as the request so user and brokers can be
 * read together. Truncating the serialised JSON would produce invalid JSON, so
 * cap the INPUT instead: drop long string values to a marker and, if still
 * over, drop keys from the end. The result is always parseable, which matters
 * because dashboards run JSONExtract over it.
 */
function encodeData(data?: Record<string, unknown>): string {
  if (!data) return '';
  const MAX = 4000;
  try {
    const flat: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'string' && v.length > 120) {
        flat[k] = `${v.slice(0, 117)}...`;
      } else if (Array.isArray(v) && k === 'brokers') {
        // The one list kept whole: which user pulled which brokers is the
        // question this field answers, so a count or first-3 sample is useless.
        flat[k] = v.map((b) => String(b).slice(0, 80));
      } else if (Array.isArray(v)) {
        // Never inline a long list: a 50-broker selection would blow the cap on
        // its own. Keep the count, which is what analysis actually needs.
        flat[k] = v.length <= 5 ? v : { n: v.length, first: v.slice(0, 3) };
      } else {
        flat[k] = v;
      }
    }
    let out = JSON.stringify(flat);
    if (out.length <= MAX) return out;
    // Verbose keys go last so they are sacrificed first. Losing a URL while
    // keeping ticker/status/ms leaves the row analysable; the reverse does not.
    const VERBOSE = new Set(['url', 'body', 'payload', 'response', 'stack', 'raw', 'sample', 'message']);
    const keys = [
      ...Object.keys(flat).filter((k) => !VERBOSE.has(k)),
      ...Object.keys(flat).filter((k) => VERBOSE.has(k)),
    ];
    while (keys.length && out.length > MAX) {
      delete flat[keys.pop() as string];
      out = JSON.stringify({ ...flat, _truncated: true });
    }
    return out;
  } catch {
    return '';
  }
}

function toRecord(entry: LogEntry, opts?: ActionOptions): Record<string, unknown> {
  const err = opts?.error ?? entry.error;
  return {
    event_id: makeId(),
    ts: entry.timestamp,
    type: LOG_TYPE,
    stream: entry.stream ?? CLIENT_STREAM,
    level: entry.level,
    context: entry.context,
    activity: activityFor(entry.context),
    message: redactSecrets((entry.message ?? '').slice(0, 2000)),
    // Ambient dimensions first so an explicit value at the call site overrides
    // them. A string `data` is already-encoded JSON from a caller that built it
    // itself, so it is passed through untouched rather than double-wrapped.
    data: redactSecrets(
      typeof entry.data === 'string'
        ? entry.data.slice(0, 4000)
        : encodeData({ ...ambient, ...(entry.data as Record<string, unknown> | undefined) })
    ),
    // Backends echo the request back in error text more often than you would
    // hope, so this is not a theoretical path.
    error: err ? redactSecrets(String(err instanceof Error ? err.message : err).slice(0, 1000)) : '',
    // Named to match the column. If data delivery later makes this MATERIALIZED
    // as the design doc specifies, ClickHouse will reject an INSERT that names
    // it — drop this line at that point and let the database compute it.
    clock_skew_s: clockSkewSeconds,
    session_id: SESSION_ID,
    // Fall back to a requestId the caller put in data: httpClient records the
    // X-Request-ID it read off the response there. Without this promotion the id
    // exists but sits inside a JSON string, where no join can reach it.
    request_id:
      opts?.requestId ||
      (typeof (entry.data as Record<string, unknown> | undefined)?.requestId === 'string'
        ? ((entry.data as Record<string, unknown>).requestId as string)
        : '') ||
      '',
    app_version: APP_VERSION,
    platform: platform(),
    user_email: userEmail(),
  };
}

// ── shipper ─────────────────────────────────────────────────────────────────
class ApiLogShipper {
  private buffer: Array<Record<string, unknown>> = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private hydrateStarted = false;
  /** Diagnostics only: how many records this page load inherited. */
  private restoredCount = 0;
  /** Records produced by THIS page load, for the Pane.Closed summary. */
  private producedCount = 0;
  /** visibilitychange and pagehide both fire on a close; only log the end once. */
  private endLogged = false;
  /**
   * Records are shipped when this many have accumulated. This is the PRIMARY
   * trigger: batching by count is what keeps the endpoint from being hit with a
   * stream of tiny POSTs.
   */
  private readonly batchSize = FLUSH_BATCH_SIZE;
  /**
   * Safety net only, measured from the FIRST record in the buffer - not reset on
   * each new one, which would let a steadily-active session defer forever.
   *
   * Without it a quiet session never reaches the count and its records are lost
   * at teardown. The visibilitychange/pagehide handlers cover that in the
   * taskpane, but the custom-functions runtime (IAD/IBD/IGD) has NO DOM events,
   * so a timer is the only thing that gets its records out at all.
   */
  private readonly maxBufferAgeMs = FLUSH_MAX_AGE_MS;
  private readonly enabled: boolean;
  /**
   * Set when AWS WAF intercepted an upload. Until then nothing is sent and the
   * records wait in the buffer: retrying straight away only feeds the rate-based
   * rule that blocked us (see utils/waf.ts).
   */
  private wafBlockedUntil = 0;

  constructor() {
    this.enabled = API_LOGGING_ENABLED === 1 && typeof fetch === 'function';

    // Before the listeners, so records parked by the previous pane are in hand
    // even if this page load is torn down immediately.
    this.restore();

    if (this.enabled && typeof window !== 'undefined') {
      try {
        // PARK, do not send. The pane is closing, but the user is almost
        // certainly opening another one a moment later - and that next page load
        // picks the records straight back up. Sending here is what made every
        // pane switch its own tiny POST.
        // The end-of-view record is written HERE rather than from App.tsx
        // because ordering decides whether it survives: this listener is
        // registered at module import, before React mounts, so any handler App
        // registers later would run AFTER persist() had already parked the
        // buffer - and the record would be stranded in memory and lost.
        const onTeardown = () => {
          this.logViewEnd();
          this.persist();
        };
        window.addEventListener('visibilitychange', () => {
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            onTeardown();
          }
        });
        window.addEventListener('pagehide', onTeardown);
      } catch {
        // No DOM events (custom-functions runtime); interval + size flush still apply.
      }
    }
  }

  /**
   * Reclaim records parked by the previous page load.
   *
   * The key is cleared as it is read, so two runtimes racing here cannot both
   * claim the same records and send them twice.
   */
  private restore(): void {
    if (!this.enabled) return;
    try {
      if (typeof localStorage === 'undefined') return;
      const parked = localStorage.getItem(BUFFER_KEY);
      if (!parked) return;
      localStorage.removeItem(BUFFER_KEY);
      const rows = JSON.parse(parked);
      if (!Array.isArray(rows) || rows.length === 0) return;
      this.buffer = rows.concat(this.buffer).slice(-BUFFER_PERSIST_CAP);
      this.restoredCount = rows.length;
      // Restored records have ALREADY waited - through a pane switch, or since
      // the last time Excel was open. Making them serve a fresh full age window
      // would compound the delay every time the taskpane reloads, so they get a
      // short grace period instead: long enough for this page load to add to the
      // batch, short enough that yesterday's records do not linger.
      if (this.buffer.length >= this.batchSize) {
        this.flush();
      } else if (this.timer === null) {
        this.timer = setTimeout(() => this.flush(), RESTORED_FLUSH_DELAY_MS);
      }
    } catch {
      // Corrupt or oversized entry - drop it rather than retry-loop on it.
      try {
        localStorage.removeItem(BUFFER_KEY);
      } catch {
        /* never throw from logging */
      }
    }
  }

  /**
   * Park the buffer for the next page load, merging with anything another
   * runtime left behind. Falls back to sending if storage is unavailable, so a
   * runtime without localStorage still delivers its records.
   */
  private persist(): void {
    if (!this.enabled || this.buffer.length === 0) return;

    const pending = this.buffer;
    this.buffer = [];
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Park FIRST and send second. Parking is synchronous and cannot half-succeed;
    // a teardown send can be cut off mid-flight with no way to know. Doing it in
    // this order means the worst case is a record sent twice, which event_id and
    // the ReplacingMergeTree collapse - rather than a record lost, which nothing
    // can recover.
    const parked = this.park(pending);

    if (!parked) {
      // No storage: sending is the only chance these records get.
      this.sendAtTeardown(pending);
      return;
    }

    // Small batch - almost certainly a pane switch, and the next page load picks
    // it straight back up. Staying quiet here is what keeps pane switching from
    // generating a POST every few seconds.
    if (pending.length < TEARDOWN_SEND_FLOOR) return;

    this.sendAtTeardown(pending);
  }

  /**
   * Record that a pane stopped being displayed.
   *
   * Named Pane.Closed, not Session.Ended, because from inside the page the two
   * are INDISTINGUISHABLE: closing Excel and clicking a different ribbon button
   * both unload taskpane.html. Claiming "session ended" would be wrong on every
   * pane switch, which is the common case by far.
   *
   * The real session end is recoverable at query time - it is simply the last
   * Pane.Closed for a user with no further activity after it. That is an honest
   * derivation from an accurate record, rather than a guess baked into the data.
   */
  private logViewEnd(): void {
    if (this.endLogged) return;
    this.endLogged = true;
    try {
      // Not a pane the user saw: Excel's background pages (ribbon commands,
      // custom functions) load this module too, never set a pane and log
      // nothing - their "Closed unknown" rows were pure noise.
      if (!getAmbientContext().pane && this.producedCount === 0) return;
      const pane = (getAmbientContext().pane as string) || 'unknown';
      logAction('Pane.Closed', `Closed ${pane}`, {
        pane,
        open_ms: Date.now() - PAGE_LOADED_AT,
        events: this.producedCount,
        unsent: this.buffer.length,
      });
    } catch {
      /* never throw from logging */
    }
  }

  /** Merge into the parked set. Returns false if storage is unusable. */
  private park(records: Array<Record<string, unknown>>): boolean {
    try {
      if (typeof localStorage === 'undefined') return false;
      let existing: Array<Record<string, unknown>> = [];
      try {
        const prior = localStorage.getItem(BUFFER_KEY);
        if (prior) {
          const rows = JSON.parse(prior);
          if (Array.isArray(rows)) existing = rows;
        }
      } catch {
        /* unreadable prior entry - overwrite it */
      }
      const merged = existing.concat(records).slice(-BUFFER_PERSIST_CAP);
      localStorage.setItem(BUFFER_KEY, JSON.stringify(merged));
      return true;
    } catch {
      // Quota exceeded or storage blocked.
      return false;
    }
  }

  /**
   * Best-effort send while the page is going away.
   *
   * Trimmed to the keepalive byte budget rather than truncated blindly: an
   * oversized keepalive request is rejected whole, so sending the first N
   * records that fit delivers something instead of nothing. Whatever is left is
   * already parked and goes out next session.
   *
   * On success the delivered records are removed from the parked set so the
   * common case does not duplicate. If teardown kills the page before that
   * cleanup runs, they are re-sent next session and deduplicated server-side by
   * event_id - the safe direction to fail in.
   */
  private sendAtTeardown(records: Array<Record<string, unknown>>): void {
    try {
      let chunk = records;
      let body = JSON.stringify(chunk);
      while (chunk.length > 1 && body.length > KEEPALIVE_MAX_BYTES) {
        chunk = chunk.slice(0, Math.floor(chunk.length / 2));
        body = JSON.stringify(chunk);
      }
      if (body.length > KEEPALIVE_MAX_BYTES) return; // one record over budget - leave it parked

      const headers = logPostHeaders();
      if (!headers) return; // not logged in - nginx would 401; stays parked
      if (Date.now() < this.wafBlockedUntil) return; // WAF backoff; stays parked

      void fetch(endpointWithFilter(), {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      })
        .then((res) => {
          if (!res.ok) return;
          const sent = new Set(chunk.map((r) => r.event_id));
          this.unpark(sent);
        })
        .catch(() => {
          /* stays parked - next session sends it */
        });
    } catch {
      /* never throw from logging */
    }
  }

  /** Drop already-delivered records from the parked set. */
  private unpark(delivered: Set<unknown>): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const prior = localStorage.getItem(BUFFER_KEY);
      if (!prior) return;
      const rows = JSON.parse(prior);
      if (!Array.isArray(rows)) return;
      const keep = rows.filter((r: Record<string, unknown>) => !delivered.has(r?.event_id));
      if (keep.length === 0) localStorage.removeItem(BUFFER_KEY);
      else localStorage.setItem(BUFFER_KEY, JSON.stringify(keep));
    } catch {
      /* never throw from logging */
    }
  }

  /**
   * Populate tokenStorage's username cache. Fire-and-forget, once: the logging
   * path must never await, but without this nothing hydrates it until the first
   * HttpClient call, and every event before that is unattributed.
   */
  private hydrateUser(): void {
    if (this.hydrateStarted) return;
    this.hydrateStarted = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mod = require('../core/auth/tokenStorage');
      void mod?.tokenStorage?.ensureUsername?.()?.catch?.(() => {
        /* never throw from logging */
      });
    } catch {
      /* storage unavailable in this runtime */
    }
  }

  enqueue(entry: LogEntry, opts?: ActionOptions): void {
    this.hydrateUser();
    if (!this.enabled) return;
    const level = opts?.level ?? entry.level;
    if (!SHIP_LEVELS.includes(level)) return;
    try {
      this.buffer.push(toRecord({ ...entry, level }, opts));
      this.producedCount++;
    } catch {
      return;
    }
    // Errors still go immediately. They are rare enough not to affect the call
    // volume, and they are the records most likely to be followed by the crash
    // that would take the rest of the buffer with it.
    if (level === 'error' || this.buffer.length >= this.batchSize) {
      this.flush();
      return;
    }
    // Armed once, by the record that starts a batch, and left alone afterwards.
    // Re-arming per record would turn this into an idle timer, which under
    // continuous activity never fires.
    if (this.timer === null) {
      this.timer = setTimeout(() => this.flush(), this.maxBufferAgeMs);
    }
  }

  /**
   * Force an immediate send, sweeping in anything a previous page load parked.
   * Without the restore, a logout flush would ship this pane's records and leave
   * the earlier panes' records sitting in storage.
   */
  flushNow(): void {
    this.restore();
    this.flush();
  }

  /** Diagnostic snapshot - what the shipper thinks its state is. */
  state(): Record<string, unknown> {
    return {
      enabled: this.enabled,
      buffered: this.buffer.length,
      // How many more records until the count trigger fires - the number to
      // watch when checking that batching works.
      untilFlush: Math.max(0, this.batchSize - this.buffer.length),
      batchSize: this.batchSize,
      // Non-zero proves the buffer survived the pane switch, which is the whole
      // point of parking it.
      restoredFromPreviousPane: this.restoredCount,
      maxBufferAgeMs: this.maxBufferAgeMs,
      ageTimerArmed: this.timer !== null,
      endpoint: API_LOGGING_ENDPOINT,
      type: LOG_TYPE,
      stream: CLIENT_STREAM,
      sessionId: SESSION_ID,
      platform: platform(),
      appVersion: APP_VERSION,
      clockSkewSeconds,
      clockSkewMeasured,
      userEmail: userEmail(),
      hasEdgeToken: Boolean(edgeToken()),
    };
  }

  /**
   * Returns the in-flight POST when one was started, so flushAndReport can await
   * the real HTTP outcome. Callers that ignore the return value are safe: a
   * rejection handler is attached below regardless.
   */
  private flush(): Promise<Response> | null {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.enabled || this.buffer.length === 0) return null;

    // No edge token or username_filter yet (before login, or idle-expired): the
    // POST would be rejected (nginx 401 / API 422) and the batch would be lost.
    // Keep the records - newest
    // BUFFER_PERSIST_CAP - for the first flush after login, and re-arm the age
    // timer so they still go out if nothing else triggers a flush. Same while a
    // WAF backoff is running, except the timer waits out the backoff instead.
    const headers = logPostHeaders();
    const wafWaitMs = this.wafBlockedUntil - Date.now();
    if (!headers || wafWaitMs > 0) {
      if (this.buffer.length > BUFFER_PERSIST_CAP) {
        this.buffer = this.buffer.slice(-BUFFER_PERSIST_CAP);
      }
      this.timer = setTimeout(() => this.flush(), wafWaitMs > 0 ? wafWaitMs : this.maxBufferAgeMs);
      return null;
    }

    const batch = this.buffer;
    this.buffer = [];

    // Backfill identity for events queued before login hydrated the cache. Only
    // fills blanks — an event already carrying a user is never relabelled.
    const who = userEmail();
    if (who) {
      for (const r of batch) {
        if (!r.user_email) r.user_email = who;
      }
    }
    let body: string;
    try {
      body = JSON.stringify(batch);
    } catch {
      return null;
    }

    try {
      // keepalive fetch, not sendBeacon: a beacon cannot carry the edge-token
      // headers nginx requires, so it would always be rejected.
      const posted = fetch(endpointWithFilter(), {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      });
      // Attached unconditionally, which is what makes it safe to also hand
      // `posted` back: the rejection is always handled here, so a caller that
      // drops the return value cannot produce an unhandled rejection.
      posted
        .then((res) => {
          // Re-measured on each flush rather than once: a machine whose clock is
          // corrected (or drifts) mid-session should not keep reporting a stale
          // offset for the rest of the Excel session.
          captureClockSkew(res as any);
          // Intercepted by AWS WAF, or refused because the session ended: the
          // batch never reached the API. Put it back (newest BUFFER_PERSIST_CAP)
          // and stay quiet for WAF_BACKOFF_MS. A session-ended 401 is NOT
          // reported from here: logout flushes logs, which would 401 again and
          // loop - the app's own data calls trigger the logout instead.
          if (isWafBlocked(res as any) || isSessionEnded(res as any)) {
            this.wafBlockedUntil = Date.now() + WAF_BACKOFF_MS;
            this.buffer = batch.concat(this.buffer).slice(-BUFFER_PERSIST_CAP);
            if (this.timer !== null) clearTimeout(this.timer);
            this.timer = setTimeout(() => this.flush(), WAF_BACKOFF_MS);
          }
        })
        .catch(() => {
          /* never throw from logging */
        });
      return posted;
    } catch {
      /* never throw from logging */
    }
    return null;
  }

  /**
   * Manual flush that reports what actually happened - for testing on dev.
   *
   * flushNow() is fire-and-forget, which is right for production but useless
   * when the question is "did the API take it?". This awaits the POST and hands
   * back the status and response body, so a failure (401, 422, wrong endpoint)
   * is visible in the console instead of silently swallowed.
   */
  async flushAndReport(): Promise<Record<string, unknown>> {
    // Include records parked by an earlier pane/session, so a manual flush sends
    // everything this machine is holding, not just this page's buffer.
    this.restore();
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const queued = this.buffer.length;
    if (!this.enabled) {
      return { ok: false, sent: 0, reason: 'shipping disabled (API_LOGGING_ENABLED is not 1)' };
    }
    if (queued === 0) {
      return { ok: true, sent: 0, reason: 'nothing buffered - trigger some actions first' };
    }
    const posted = this.flush();
    if (!posted) return { ok: false, sent: 0, reason: 'flush did not start a request' };
    try {
      const res = await posted;
      let preview = '';
      try {
        preview = (await res.text()).slice(0, 300);
      } catch {
        /* body already consumed or unreadable - status is the useful part */
      }
      return {
        ok: res.ok,
        status: res.status,
        sent: queued,
        endpoint: API_LOGGING_ENDPOINT,
        db: LOG_TYPE,
        response: preview,
      };
    } catch (e) {
      // Almost always CORS, a wrong endpoint, or the proxy not routing
      // /consensus_v2/* - all of which surface here rather than vanishing.
      return { ok: false, sent: queued, error: String(e), endpoint: API_LOGGING_ENDPOINT };
    }
  }
}

const shipper = new ApiLogShipper();

/** Called by logger.ts so existing logger.info/warn/error calls also reach the API. */
export function shipToApi(entry: LogEntry): void {
  shipper.enqueue(entry);
}

/**
 * Record one user action — one row per PROCESS, not per row touched. Unlink
 * across 100 cells is a single event with {cells:100}; unlinking 1 cell is a
 * single event with {cells:1}. Per-cell logging would make one user gesture
 * dominate the entire log volume.
 *
 *   logAction('Unlink.Applied', 'Unlinked formulas', { scope: 'worksheet', cells: 100, ms: 812 });
 *
 * `context` is LowCardinality(String): keep it a fixed Module.Action vocabulary
 * and never interpolate values (a ticker or sheet name) into it. Params go in
 * `data`.
 */
export function logAction(
  context: string,
  message: string,
  data?: Record<string, unknown>,
  opts?: ActionOptions
): void {
  try {
    shipper.enqueue(
      {
        timestamp: new Date().toISOString(),
        level: opts?.level ?? 'info',
        context,
        message,
        data,
      } as LogEntry,
      opts
    );
  } catch {
    /* never throw from logging */
  }
}

/**
 * The broker fields every broker-aware event carries, in one shape, so a single
 * query answers "which user pulled which brokers" across Insert Data, Insert
 * Formula and Broker Revisions:
 *
 *   JSONExtract(data, 'brokers', 'Array(String)')
 *
 * `brokers` holds real broker names only. "Average" is the consensus, not a
 * broker, so it is dropped: an empty list means consensus across all brokers.
 * Spread it LAST in the call's data, so that if a row ever overflows the cap,
 * encodeData sacrifices the list before the timings.
 */
export function brokerLogFields(brokers: Array<string | undefined | null> | string | undefined | null): {
  broker_count: number;
  brokers: string[];
} {
  const list = (Array.isArray(brokers) ? brokers : [brokers])
    .map((b) => String(b ?? '').trim())
    .filter((b) => b && b.toLowerCase() !== 'average');
  return { broker_count: list.length, brokers: list };
}

/**
 * Send everything buffered right now, including anything parked by an earlier
 * page load.
 *
 * For logout specifically. Must be called BEFORE credentials are cleared:
 * user_email is backfilled from the cached username at flush time, so flushing
 * afterwards would strip the identity from the very records that explain what
 * the user did in the session that just ended.
 */
export function flushLogsNow(): void {
  try {
    shipper.flushNow();
  } catch {
    /* never throw from logging */
  }
}

export const ACTION_SESSION_ID = SESSION_ID;

/**
 * Test seam only. Redaction is the one piece here whose failure is silent and
 * unrecoverable - a leaked credential cannot be un-logged - so it is worth
 * being able to assert on directly rather than inferring from a shipped record.
 */
export const __redactForTest = redactSecrets;

// -- console hook ----------------------------------------------------------
// Records ship at FLUSH_BATCH_SIZE (100) or after FLUSH_MAX_AGE_MS (10 min),
// plus immediately on errors and at logout. To send what is waiting NOW, from
// the taskpane DevTools console:
//
//   await __insyncLog.flush()   send everything buffered - addin-logs (incl.
//                               records parked by earlier panes) AND /telemetry -
//                               and REPORT the addin-logs HTTP result
//   __insyncLog.status()        buffered count, untilFlush, endpoint, user, edge token
//
// flush() resolves to { ok, status, sent, endpoint, db, response, telemetrySent }.
// A 401 means nginx rejected the edge token (or DD_BASIC_AUTH is missing from
// its env); a 404/HTML response means the proxy is not routing /consensus_v2/*.
//
// flush() and status() exist in EVERY build: they only send records this user's
// add-in already produced. Dev builds (IS_PROD !== 1) also get:
//
//   __insyncLog.action()        emit one test event and send it
//   __insyncLog.action("Unlink.Applied", "manual", { cells: 7 })
//   await __insyncLog.seed(120) emit N synthetic events - crosses the batch
//                               threshold, so it also proves count-batching works
//
// Those two are absent from prod deliberately: they synthesise rows, and a log
// store people query for user activity must not accept hand-written entries
// from a browser console.
//
// Attached to globalThis as well as window so it is reachable from the
// custom-functions runtime (IAD/IBD/IGD), which has no window.
let telemetryFlush: (() => number) | null = null;

/** logger.ts hands over the /telemetry flush (it imports this module, not vice versa). */
export function registerTelemetryFlush(fn: () => number): void {
  telemetryFlush = fn;
}

try {
  const hook: Record<string, unknown> = {
    status: () => shipper.state(),
    flush: async () => {
      let telemetrySent = 0;
      try {
        telemetrySent = telemetryFlush ? telemetryFlush() : 0;
      } catch {
        /* never throw from logging */
      }
      return { ...(await shipper.flushAndReport()), telemetrySent };
    },
  };
  if (IS_PROD !== 1) {
    hook.action = (
      context = 'Ribbon.Clicked',
      message = 'manual console test',
      data?: Record<string, unknown>
    ) => {
      logAction(context, message, data ?? { manual: true });
      return shipper.flushAndReport();
    };
    /**
     * Emit n synthetic events, then flush. Default 120 deliberately exceeds
     * FLUSH_BATCH_SIZE, so the count trigger fires on its own partway through
     * and the explicit flush only sends the remainder - which is the quickest
     * way to confirm batching is working rather than just that POSTs work.
     */
    hook.seed = (n = 120) => {
      for (let i = 0; i < n; i++) {
        logAction('Ribbon.Clicked', 'seeded test event', { seq: i + 1, seeded: true });
      }
      return shipper.flushAndReport();
    };
  }
  if (typeof globalThis !== 'undefined') (globalThis as any).__insyncLog = hook;
  if (typeof window !== 'undefined') (window as any).__insyncLog = hook;
} catch {
  /* never throw from logging */
}
